import Cookie from 'js-cookie';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { useViewer } from '@vimeo/viewer';

import { defaultSettings } from './default-settings';
import type { OrionSettings } from './default-settings';
import { createOrion } from '../internal/core';
import type { Orion, OrionIdentity } from '../internal/core';
import type { OrionRequestAttributes } from '../internal/core/model/configuration';
import { createIndexedDBPersistenceManager } from '../internal/web';
import { isCrawler } from '../internal/web/isCrawler';
import { getDeviceType } from '../utils/get-device-type';
import { picoXLocalStorageId } from '../utils/local-storage-id';
import { emptyLogger, verboseLogger } from '../utils/logger';

const ORION_URL = {
  production: 'https://orion.bendingspoons.com',
  staging: 'https://orion.bendingspoons.com',
};
const BSP_ID = 'vimeo_web';

/// Default TTL of the settings cache.
const TWO_HOURS_IN_MILLISECONDS = 1000 * 60 * 60 * 2;
const MINUTES_IN_MILLISECONDS = 1000 * 60;
/// Timeout for Orion requests.
const FETCH_TIMEOUT_MILLISECONDS = 10 * 1000;

/**
 * Type of context returned by the OrionProvider. It exposes the Orion client instance when it's ready, the latest cached identity
 * and the flag `isLoadingResponse` indicating if Orion is currently fetching the identity or not.
 * If Orion doesn't answer within the fetch timeout (10 seconds currently), `isLoadingResponse` becomes false so that callers
 * can stop waiting for the response.
 */
export type OrionWrapper = {
  client: Orion<OrionSettings> | undefined;
  identity: OrionIdentity<OrionSettings>;
  isLoadingResponse: boolean;
};

const OrionContext = createContext<OrionWrapper>({
  client: undefined,
  identity: {
    isDefaultIdentity: true,
    settings: defaultSettings,
    segmentations: {},
    settingsHash: '',
  },
  isLoadingResponse: true,
});

/// Hook to access the Orion client instance.
export const useOrion = () => useContext(OrionContext);

type OrionProviderProps = {
  children: React.ReactNode;
  isLoggingEnabled?: boolean;
};

function defaultIsDevelopment(): boolean {
  // Local and CI environments
  return (
    window.location.hostname === 'vimeo.dev' || window.location.hostname.endsWith('.vimeows.com')
  );
}

function getMinutesElapsedSinceRegistration(createdTime: string | null | undefined): number | null {
  if (!createdTime) {
    return null;
  }
  const createdAt = Date.parse(createdTime);
  if (Number.isNaN(createdAt)) {
    return null;
  }
  const diffInMinutes = Math.floor((Date.now() - createdAt) / MINUTES_IN_MILLISECONDS);
  return Math.max(0, diffInMinutes);
}

export const OrionProvider: React.FC<OrionProviderProps> = ({
  children,
  isLoggingEnabled = false,
}) => {
  const logger = isLoggingEnabled ? verboseLogger : emptyLogger;
  const viewer = useViewer();

  // If the viewer is not loaded, userId is undefined (distinct from null = logged-out).
  // The identity-fetch effect waits for userId !== undefined before running.
  const isLoading = !viewer;
  let userId: string | null | undefined;
  let vuid: string | undefined;
  let teamOwnerId: string | null = null;
  let actorId: string | null = null;
  let tier: string | null = null;
  let vimeoProductId: string | null = null;

  if (!isLoading) {
    userId = viewer?.user?.id?.toString() ?? null;
    vuid = viewer.vuid;
    teamOwnerId = viewer.teamUser?.ownerId?.toString() ?? null;
    actorId = teamOwnerId ? `T_${teamOwnerId}` : userId ? `U_${userId}` : null;
    tier = viewer.teamUser?.accountType?.toString() ?? viewer.user?.account?.toString() ?? null;
    vimeoProductId = viewer.user?.productId?.toString() ?? null;
  }

  // Refs keep the latest user attributes so that the getter closures passed to
  // createOrion always return up-to-date values without requiring a new instance.
  const userIdRef = useRef(userId);
  const vuidRef = useRef(vuid);
  const teamOwnerIdRef = useRef(teamOwnerId);
  const actorIdRef = useRef(actorId);
  const tierRef = useRef(tier);
  const vimeoProductIdRef = useRef(vimeoProductId);
  const viewerRef = useRef(viewer);
  userIdRef.current = userId;
  vuidRef.current = vuid;
  teamOwnerIdRef.current = teamOwnerId;
  actorIdRef.current = actorId;
  tierRef.current = tier;
  vimeoProductIdRef.current = vimeoProductId;
  viewerRef.current = viewer;

  // Client is stored in state so the identity-fetch effect can depend on it.
  const [orionClient, setOrionClient] = useState<Orion<OrionSettings> | undefined>(undefined);
  const initStartedRef = useRef(false);
  // Incremented on init failure to re-trigger the init effect. Resetting initStartedRef alone
  // is not enough because the effect deps would not have changed, so React would not re-run it.
  const [initRetryCount, setInitRetryCount] = useState(0);

  const [identity, setIdentity] = useState<OrionIdentity<OrionSettings>>({
    settings: defaultSettings,
    segmentations: {},
    settingsHash: '',
    isDefaultIdentity: true,
  });
  const [isLoadingResponse, setIsLoadingResponse] = useState(true);

  // Initialize a single Orion instance once the viewer is available.
  // User/context changes are handled by the identity-fetch effect below.
  useEffect(() => {
    if (isLoading || initStartedRef.current) {
      return;
    }
    initStartedRef.current = true;

    const initOrion = async () => {
      try {
        const env = defaultIsDevelopment() ? 'staging' : 'production';
        const persistenceManager = await createIndexedDBPersistenceManager<OrionSettings>();
        const orion = await createOrion<OrionSettings>({
          goesThroughOrionProxy: false,
          appId: BSP_ID,
          // When adding new attributes consider using them in the `userIdGenerator` function if they are relevant
          // to determine if the settings should be refreshed.
          appSpecificAttributeGetters: {
            vimeoUserId: () => Promise.resolve(userIdRef.current),
            vuid: () => Promise.resolve(vuidRef.current),
            teamOwnerId: () => Promise.resolve(teamOwnerIdRef.current),
            actorId: () => Promise.resolve(actorIdRef.current),
            tier: () => Promise.resolve(tierRef.current),
            vimeoProductId: () => Promise.resolve(vimeoProductIdRef.current),
            minutesElapsedSinceRegistration: () =>
              Promise.resolve(
                getMinutesElapsedSinceRegistration(viewerRef.current?.user?.createdTime),
              ),
            clientEnvironment: () =>
              Promise.resolve(defaultIsDevelopment() ? 'staging' : 'production'),
          },
          persistenceManager,
          defaultSettings,
          defaultSegmentations: {},
          deviceAttributeGetters: {
            isLoggedIn: () => Promise.resolve(!!userIdRef.current),
            localStorageId: picoXLocalStorageId(),
            deviceType: getDeviceType,
          },
          logger,
          baseUrl: ORION_URL[env],
          // Return defaults for crawlers to avoid wasting time waiting for Orion if some blocking calls are made.
          shouldReturnDefaultsImmediately: isCrawler(),
          fetcher: (...args) => fetch(...args),
          // Add here relevant attributes to identify the user.
          // The set of keys should be the minimum set of items passed to Orion that make the user unique.
          // Keep in mind that some keys might not always be available, so the hash function should be able to handle that.
          // Note: the value returned by this generator is only used by the local cache to check if it needs refreshing,
          // it's not sent to Orion.
          userIdGenerator: (orionRequestAttributes: OrionRequestAttributes): Promise<string> => {
            const keys = ['localStorageId', 'vimeoUserId', 'vuid', 'teamOwnerId'];

            return Promise.resolve(
              keys
                .filter((key) => orionRequestAttributes[key] !== undefined)
                .map((key) => orionRequestAttributes[key])
                .join('_'),
            );
          },
          identityCacheTtlInMs: TWO_HOURS_IN_MILLISECONDS,
        });
        setOrionClient(orion);
      } catch (e) {
        logger.error('Failed to initialize Orion client', e);
        initStartedRef.current = false;
        setInitRetryCount((c) => c + 1);
      }
    };

    void initOrion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, initRetryCount]);

  // Fetch or re-fetch the identity when the client is ready or the user changes.
  // Because the attribute getters read from refs, the userIdGenerator produces a
  // new cache key on user change, so Orion automatically fetches fresh settings.
  useEffect(() => {
    if (!orionClient || userId === undefined) {
      return;
    }

    setIsLoadingResponse(true);

    const fetchIdentity = async () => {
      try {
        const fetchedIdentity = await orionClient.identity.get();
        setIdentity({
          settings: fetchedIdentity.settings as OrionSettings,
          segmentations: fetchedIdentity.segmentations,
          settingsHash: fetchedIdentity.settingsHash,
          isDefaultIdentity: fetchedIdentity.isDefaultIdentity,
        });
        setIsLoadingResponse(false);
        logger.info('Orion identity ready');
      } catch (e) {
        logger.error('Failed to fetch Orion identity', e);
      }
    };

    // Timeout so that Orion consumers can stop waiting if the response never arrives.
    let orionTimeout: number;
    const timeoutPromise = new Promise(
      (resolve) =>
        (orionTimeout = setTimeout(() => {
          setIsLoadingResponse((current) => {
            if (current) {
              logger.error(`Timeout: Orion identity was not received within the timeout.`);
              return false;
            }
            return current;
          });
          resolve(0);
        }, FETCH_TIMEOUT_MILLISECONDS)),
    );

    void Promise.race([fetchIdentity(), timeoutPromise]).finally(() => clearTimeout(orionTimeout));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orionClient, userId, teamOwnerId]);

  // Sync Orion Cookies from setting for backend consumption
  const orionFreeOverride = identity.settings.orion_free_override;

  useEffect(() => {
    if (!isLoadingResponse && orionFreeOverride) {
      Cookie.set('orion_free_override', '1', {
        sameSite: 'none',
        secure: true,
      });
    }
  }, [isLoadingResponse, orionFreeOverride]);

  return (
    <OrionContext.Provider
      value={{
        client: orionClient,
        identity,
        isLoadingResponse,
      }}
    >
      {children}
    </OrionContext.Provider>
  );
};
